import { isEmpty, pickBy, snakeCase } from 'lodash';
import { compile } from 'path-to-regexp';
import qs from 'qs';

import { AssignedPermission } from '@/apis/gin/boundary-database/users/assignments.dto';
import { EmissionSourceCategory, EmissionSourceSubCategory } from '@/app/org-cfv/src/enums/EmissionSourceEnum';
import { DataEntryTabType } from '@/app/product-cfv/data-entry/_components/DataEntry';
import { DetailsPageTabType } from '@/app/project-settings/product-series/details/ProductSeriesDetailsPage';
import { SCROLL_TO_KEY } from '@/constants/common';
import { NextTFunction } from '@/i18n/client';
import { languages } from '@/i18n/settings';
import { AI_MAPPING_CATEGORY, ScenarioConfigurationType } from '@/typings/common';
import { PROJECT_ADMIN_ROLES, Role } from '@/typings/user';
import { isServer } from '@/utils/common';

import { Route } from './enum';
import { SidebarGroup } from './typings';

export const LANG_REGEX = `/(${languages.join('|')})`;

export const appendQueryString = (
  path: string,
  queryParams: Record<string, string | number | undefined | null> = {},
  withBoundaryId = true,
) => {
  if (!isServer && withBoundaryId) {
    const urlParams = new URLSearchParams(location.search);
    const boundaryId = urlParams.get('boundaryId');
    if (boundaryId) {
      queryParams.boundaryId = boundaryId;
    }
  }

  const queryString = isEmpty(pickBy(queryParams, (v) => v !== undefined)) ? '' : `?${qs.stringify(queryParams)}`;
  return `${path}${queryString}`;
};

const ORGANIZATIONAL_STRUCTURE_MANAGEMENT_ROUTE_CONFIGS = {
  [Route.ORGANIZATIONAL_STRUCTURE_MANAGEMENT]: {
    title: (t: NextTFunction) => t('program-management.organizationalStructureManagement'),
    description: (t: NextTFunction) => t('common.infoBoxOSM'),
    roles: [Role.CMS_ADMIN, Role.PROGRAM_MANAGER],
    permissions: undefined,
    url: '/organizational-structure-management',
    getFullUrl: function () {
      return appendQueryString(this.url, {}, false);
    },
  },
  [Route.OSM_PARENT_ORGANIZATIONAL_UNIT_CREATE]: {
    title: (t: NextTFunction) => t('program-management.createMainParentUnit'),
    roles: [Role.CMS_ADMIN, Role.PROGRAM_MANAGER],
    permissions: undefined,
    url: '/organizational-structure-management/create-parent',
    getFullUrl: function () {
      return appendQueryString(this.url);
    },
  },
  [Route.OSM_ORGANIZATIONAL_UNIT_CREATE]: {
    title: (t: NextTFunction) => t('program-management.createOrganizationalUnit'),
    roles: [Role.CMS_ADMIN, Role.PROGRAM_MANAGER],
    permissions: undefined,
    url: '/organizational-structure-management/create',
    getFullUrl: function ({ parentUnitId }: { parentUnitId?: string } = {}) {
      return appendQueryString(this.url, { parentUnitId });
    },
  },
  [Route.OSM_PARENT_ORGANIZATIONAL_UNIT_VIEW]: {
    title: (t: NextTFunction) => t('program-management.mainParentUnitInformation'),
    roles: [Role.CMS_ADMIN, Role.PROGRAM_MANAGER],
    permissions: undefined,
    url: '/organizational-structure-management/view-parent',
    getFullUrl: function ({ orgUnitId }: { orgUnitId: string }) {
      return appendQueryString(this.url, { orgUnitId });
    },
  },
  [Route.OSM_PARENT_ORGANIZATIONAL_UNIT_EDIT]: {
    title: (t: NextTFunction) => t('program-management.editMainParentUnit'),
    roles: [Role.CMS_ADMIN, Role.PROGRAM_MANAGER],
    permissions: undefined,
    url: '/organizational-structure-management/view-parent/edit',
    getFullUrl: function ({ orgUnitId }: { orgUnitId: string }) {
      return appendQueryString(this.url, { orgUnitId });
    },
  },
  [Route.OSM_ORGANIZATIONAL_UNIT_VIEW]: {
    title: (t: NextTFunction) => t('program-management.organizationalUnitInformation'),
    roles: [Role.CMS_ADMIN, Role.PROGRAM_MANAGER],
    permissions: undefined,
    url: '/organizational-structure-management/view',
    getFullUrl: function ({ orgUnitId }: { orgUnitId: string }) {
      return appendQueryString(this.url, { orgUnitId });
    },
  },
  [Route.OSM_ORGANIZATIONAL_UNIT_EDIT]: {
    title: (t: NextTFunction) => t('program-management.editOrganizationalUnit'),
    roles: [Role.CMS_ADMIN, Role.PROGRAM_MANAGER],
    permissions: undefined,
    url: '/organizational-structure-management/view/edit',
    getFullUrl: function ({ orgUnitId }: { orgUnitId: string }) {
      return appendQueryString(this.url, { orgUnitId });
    },
  },
};

const SUPPLIER_MANAGEMENT_ROUTE_CONFIGS = {
  [Route.SUPPLIER_MANAGEMENT_DATA_REQUEST]: {
    title: (t: NextTFunction) => t('program-management.dataRequest'),
    roles: [Role.CMS_ADMIN, Role.ACCOUNT_ADMIN, Role.PROGRAM_MANAGER],
    description: (t: NextTFunction) => t('common.infoboxSupplierMgmt'),
    permissions: undefined,
    url: '/supplier-management/data-request',
    getFullUrl: function () {
      return appendQueryString(this.url);
    },
  },
  [Route.SUPPLIER_MANAGEMENT_DATA_REQUEST_CREATE]: {
    title: (t: NextTFunction) => t('program-management.createDataRequest'),
    roles: [Role.CMS_ADMIN, Role.ACCOUNT_ADMIN, Role.PROGRAM_MANAGER],
    permissions: undefined,
    url: '/supplier-management/data-request/create',
    getFullUrl: function () {
      return appendQueryString(this.url);
    },
  },
  [Route.SUPPLIER_MANAGEMENT_DATA_REQUEST_DETAILS]: {
    title: (t: NextTFunction) => t('program-management.viewDetails'),
    roles: [Role.CMS_ADMIN, Role.ACCOUNT_ADMIN, Role.PROGRAM_MANAGER],
    permissions: undefined,
    url: '/supplier-management/data-request/detail',
    getFullUrl: function ({ requestId }: { requestId: string }) {
      return appendQueryString(this.url, { requestId });
    },
  },
  [Route.SUPPLIER_DATA_ENTRY]: {
    title: () => '',
    roles: undefined,
    permissions: undefined,
    url: '/supplier-data-entry',
    getFullUrl: function () {
      return appendQueryString(this.url, {}, false);
    },
  },
};

const MEMBER_CENTER_ROUTE_CONFIGS = {
  [Route.MEMBER_PROFILE]: {
    title: (t: NextTFunction) => t('account-management.memberCenter'),
    roles: [...PROJECT_ADMIN_ROLES, Role.DEPARTMENT_MANAGER, Role.DEPARTMENT_USER, Role.DATA_COLLECTOR],
    permissions: undefined,
    url: '/user/profile',
    getFullUrl: function () {
      return appendQueryString(this.url);
    },
  },
  [Route.MEMBER_MANUAL]: {
    title: (t: NextTFunction) => t('account-management.userManual'),
    roles: [...PROJECT_ADMIN_ROLES, Role.DEPARTMENT_MANAGER, Role.DEPARTMENT_USER, Role.DATA_COLLECTOR],
    permissions: undefined,
    url: '/user/manual',
    getFullUrl: function () {
      return appendQueryString(this.url);
    },
  },
  [Route.MEMBER_CHANGE_PASSWORD]: {
    title: (t: NextTFunction) => t('account-management.changePassword'),
    roles: [...PROJECT_ADMIN_ROLES, Role.DEPARTMENT_MANAGER, Role.DEPARTMENT_USER, Role.DATA_COLLECTOR],
    permissions: undefined,
    url: '/user/profile/change-password',
    getFullUrl: function () {
      return appendQueryString(this.url);
    },
  },
  [Route.MEMBER_BILLING_INFORMATION]: {
    title: (t: NextTFunction) => t('account-management.billingInformation'),
    roles: [...PROJECT_ADMIN_ROLES, Role.DEPARTMENT_MANAGER, Role.DEPARTMENT_USER, Role.DATA_COLLECTOR],
    permissions: undefined,
    url: '/user/billing-information',
    getFullUrl: function () {
      return appendQueryString(this.url);
    },
  },
  [Route.MEMBER_NOTIFICATION_SETTINGS]: {
    title: (t: NextTFunction) => t('account-management.notificationSettings'),
    roles: [...PROJECT_ADMIN_ROLES, Role.DEPARTMENT_MANAGER, Role.DEPARTMENT_USER, Role.DATA_COLLECTOR],
    permissions: undefined,
    url: '/user/notification-settings',
    getFullUrl: function () {
      return appendQueryString(this.url);
    },
  },
  [Route.MEMBER_FAQ_AND_SUPPORT]: {
    title: (t: NextTFunction) => t('account-management.faqTechSupport'),
    roles: [...PROJECT_ADMIN_ROLES, Role.DEPARTMENT_MANAGER, Role.DEPARTMENT_USER, Role.DATA_COLLECTOR],
    permissions: undefined,
    url: '/user/faq-and-support',
    getFullUrl: function () {
      return appendQueryString(this.url);
    },
  },
  [Route.MEMBER_LEGAL_DOCUMENTS]: {
    title: (t: NextTFunction) => t('account-management.legalDocuments'),
    roles: [...PROJECT_ADMIN_ROLES, Role.DEPARTMENT_MANAGER, Role.DEPARTMENT_USER, Role.DATA_COLLECTOR],
    permissions: undefined,
    url: '/user/legal-documents',
    getFullUrl: function () {
      return appendQueryString(this.url);
    },
  },
};

const ORG_CFV_ROUTE_CONFIGS = {
  [Route.ORG_CFV]: {
    title: (t: NextTFunction) => t('sidebar.orgCFV'),
    roles: [...PROJECT_ADMIN_ROLES, Role.DEPARTMENT_MANAGER, Role.DEPARTMENT_USER, Role.DATA_COLLECTOR],
    permissions: undefined,
    url: '/org-cfv',
    getFullUrl: function ({ page, limit }: { page?: number; limit?: number } = {}) {
      return appendQueryString(this.url, { limit, page });
    },
  },
  [Route.ORG_CFV_PROJECT_EMISSION_IDENTIFICATION]: {
    title: (t: NextTFunction) => t('sidebar.emissionIdentification'),
    roles: [...PROJECT_ADMIN_ROLES, Role.DEPARTMENT_MANAGER, Role.DEPARTMENT_USER, Role.DATA_COLLECTOR],
    description: (t: NextTFunction) => t('common.infoBoxCollectDataiOEmissionIdentification'),
    permissions: undefined,
    url: '/org-cfv/:projectId/emission-identification',
    getFullUrl: function ({ projectId }: { projectId: string }) {
      return appendQueryString(compile(this.url)({ projectId }));
    },
  },
  [Route.ORG_CFV_PROJECT_EMISSION_IDENTIFICATION_EDIT]: {
    title: (t: NextTFunction) => t('common.editEmissionIdentification'),
    roles: PROJECT_ADMIN_ROLES,
    permissions: undefined,
    url: '/org-cfv/:projectId/emission-identification/edit',
    getFullUrl: function ({ projectId }: { projectId: string }) {
      return appendQueryString(compile(this.url)({ projectId }));
    },
  },
  [Route.ORG_CFV_PROJECT_EMISSION_SOURCES]: {
    title: (t: NextTFunction) => t('sidebar.sidebarEmission'),
    roles: [...PROJECT_ADMIN_ROLES, Role.DEPARTMENT_MANAGER, Role.DEPARTMENT_USER, Role.DATA_COLLECTOR],
    description: (t: NextTFunction) => t('common.infoBoxCollectDataiOEmission'),
    permissions: undefined,
    url: '/org-cfv/:projectId/emission-sources',
    getFullUrl: function ({
      projectId,
      category,
      subCategoryList,
      expandList,
      pageList,
      limitList,
    }: {
      projectId: string;
      category?: EmissionSourceCategory;
      subCategoryList?: string;
      expandList?: string;
      pageList?: string;
      limitList?: string;
    }) {
      return appendQueryString(compile(this.url)({ projectId }), {
        category,
        subCategoryList,
        expandList,
        pageList,
        limitList,
      });
    },
  },
  [Route.ORG_CFV_PROJECT_CREATE_EMISSION_SOURCE]: {
    title: (t: NextTFunction) => t('org-cfv-create-emission.createNewEmission'),
    roles: PROJECT_ADMIN_ROLES,
    permissions: undefined,
    url: '/org-cfv/:projectId/emission-sources/create',
    getFullUrl: function ({ projectId, subCategory }: { projectId: string; subCategory: EmissionSourceSubCategory }) {
      return appendQueryString(compile(this.url)({ projectId }), { subCategory });
    },
  },
  [Route.ORG_CFV_PROJECT_RELEVANT_EMISSION_SOURCE]: {
    title: (t: NextTFunction) => t('org-cfv-create-emission.emissionSettings'),
    roles: PROJECT_ADMIN_ROLES,
    permissions: undefined,
    url: '/org-cfv/:projectId/emission-sources/relevant',
    getFullUrl: function ({ projectId, subCategory }: { projectId: string; subCategory: EmissionSourceSubCategory }) {
      return appendQueryString(compile(this.url)({ projectId }), { subCategory });
    },
  },
  [Route.ORG_CFV_PROJECT_EDIT_EMISSION_SOURCE]: {
    title: (t: NextTFunction) => t('org-cfv-create-emission.editEmission'),
    roles: PROJECT_ADMIN_ROLES,
    permissions: undefined,
    url: '/org-cfv/:projectId/emission-sources/:emissionSourceId/edit',
    getFullUrl: function ({ projectId, emissionSourceId }: { projectId: string; emissionSourceId: string }) {
      return appendQueryString(compile(this.url)({ projectId, emissionSourceId }));
    },
  },
  [Route.ORG_CFV_PROJECT_VIEW_EMISSION_SOURCE]: {
    title: (t: NextTFunction) => t('org-cfv-create-emission.viewEmission'),
    roles: [...PROJECT_ADMIN_ROLES, Role.DEPARTMENT_MANAGER, Role.DEPARTMENT_USER, Role.DATA_COLLECTOR],
    permissions: undefined,
    url: '/org-cfv/:projectId/emission-sources/:emissionSourceId',
    getFullUrl: function ({
      projectId,
      emissionSourceId,
      relatedId,
    }: {
      projectId: string;
      emissionSourceId: string;
      relatedId?: string;
    }) {
      return appendQueryString(compile(this.url)({ projectId, emissionSourceId }), { relatedId });
    },
  },
  [Route.ORG_CFV_PROJECT_ACTIVITY_DATA_ENTRY]: {
    title: (t: NextTFunction) => t('org-cfv-data-entry.activityData'),
    roles: [...PROJECT_ADMIN_ROLES, Role.DEPARTMENT_MANAGER, Role.DEPARTMENT_USER, Role.DATA_COLLECTOR],
    description: (t: NextTFunction) => t('common.infoBoxCollectDataiOActivityDataEntry'),
    permissions: undefined,
    url: '/org-cfv/:projectId/activity-data-entry',
    getFullUrl: function ({
      projectId,
      category,
      subCategory,
      emissionId,
      page,
      limit,
    }: {
      projectId: string;
      category?: EmissionSourceCategory;
      subCategory?: EmissionSourceSubCategory;
      emissionId?: string;
      page?: number;
      limit?: number;
    }) {
      return appendQueryString(compile(this.url)({ projectId }), { category, emissionId, subCategory, page, limit });
    },
  },
  [Route.ORG_CFV_PROJECT_CREATE_ACTIVITY_DATA_ENTRY]: {
    title: (t: NextTFunction) => t('org-cfv-data-entry.addNewActivityData'),
    roles: [...PROJECT_ADMIN_ROLES, Role.DEPARTMENT_MANAGER, Role.DEPARTMENT_USER, Role.DATA_COLLECTOR],
    permissions: undefined,
    url: '/org-cfv/:projectId/activity-data-entry/create',
    getFullUrl: function ({
      projectId,
      emissionSourceId,
      subCategory,
    }: {
      projectId: string;
      emissionSourceId?: string;
      subCategory: EmissionSourceSubCategory;
    }) {
      return appendQueryString(compile(this.url)({ projectId }), { emissionSourceId, subCategory });
    },
  },
  [Route.ORG_CFV_PROJECT_EDIT_ACTIVITY_DATA_ENTRY]: {
    title: (t: NextTFunction) => t('org-cfv-data-entry.editActivityData'),
    roles: [...PROJECT_ADMIN_ROLES, Role.DEPARTMENT_MANAGER, Role.DEPARTMENT_USER, Role.DATA_COLLECTOR],
    permissions: undefined,
    url: '/org-cfv/:projectId/activity-data-entry/edit',
    getFullUrl: function ({ projectId, activityId }: { projectId: string; activityId: string }) {
      return appendQueryString(compile(this.url)({ projectId }), { activityId });
    },
  },
  [Route.ORG_CFV_PROJECT_EDIT_ACTIVITY_DATA_ENTRY_BULK]: {
    title: (t: NextTFunction) => t('org-cfv-data-entry.editActivityData'),
    roles: [...PROJECT_ADMIN_ROLES, Role.DEPARTMENT_MANAGER, Role.DEPARTMENT_USER, Role.DATA_COLLECTOR],
    permissions: undefined,
    url: '/org-cfv/:projectId/activity-data-entry/bulk-input/edit',
    getFullUrl: function ({
      projectId,
      emissionSourceId,
      approvalId,
      subCategory,
    }: {
      projectId: string;
      emissionSourceId: string;
      approvalId: string;
      subCategory: EmissionSourceSubCategory;
    }) {
      return appendQueryString(compile(this.url)({ projectId }), { emissionSourceId, approvalId, subCategory });
    },
  },
  [Route.ORG_CFV_PROJECT_CREATE_ACTIVITY_DATA_ENTRY_BULK]: {
    title: (t: NextTFunction) => t('org-cfv-data-entry.addNewActivityData'),
    roles: [...PROJECT_ADMIN_ROLES, Role.DEPARTMENT_MANAGER, Role.DEPARTMENT_USER, Role.DATA_COLLECTOR],
    permissions: undefined,
    url: '/org-cfv/:projectId/activity-data-entry/bulk-input',
    getFullUrl: function ({
      projectId,
      emissionSourceId,
      subCategory,
    }: {
      projectId: string;
      emissionSourceId: string;
      subCategory: EmissionSourceSubCategory;
    }) {
      return appendQueryString(compile(this.url)({ projectId }), { emissionSourceId, subCategory });
    },
  },
  [Route.ORG_CFV_PROJECT_ACTIVITY_DATA_ENTRY_DETAILS]: {
    title: (t: NextTFunction) => t('org-cfv-data-entry.activityDataDetails'),
    roles: [...PROJECT_ADMIN_ROLES, Role.DEPARTMENT_MANAGER, Role.DEPARTMENT_USER, Role.DATA_COLLECTOR],
    permissions: undefined,
    url: '/org-cfv/:projectId/activity-data-entry/:activityId/details',
    getFullUrl: function ({
      projectId,
      activityId,
      emissionId,
      approvalId,
      subCategory,
    }: {
      projectId: string;
      activityId: string;
      emissionId: string;
      approvalId: string;
      subCategory: EmissionSourceSubCategory;
    }) {
      return appendQueryString(compile(this.url)({ projectId, activityId }), { emissionId, approvalId, subCategory });
    },
  },
  [Route.ORG_CFV_PROJECT_ACTIVITY_DATA_APPROVAL]: {
    title: (t: NextTFunction) => t('sidebar.activityDataApproval'),
    roles: [...PROJECT_ADMIN_ROLES, Role.DEPARTMENT_MANAGER],
    description: (t: NextTFunction) => t('common.infoBoxDataApprovaliOActivityDataApproval'),
    permissions: undefined,
    url: '/org-cfv/:projectId/activity-data-approval',
    getFullUrl: function ({ projectId, subCategory }: { projectId: string; subCategory?: EmissionSourceSubCategory }) {
      return appendQueryString(compile(this.url)({ projectId }), { subCategory });
    },
  },
  [Route.ORG_CFV_PROJECT_ACTIVITY_DATA_APPROVAL_DETAILS]: {
    title: (t: NextTFunction) => t('org-cfv-data-entry.activityDataDetails'),
    roles: [...PROJECT_ADMIN_ROLES, Role.DEPARTMENT_MANAGER],
    permissions: undefined,
    url: '/org-cfv/:projectId/activity-data-approval/:activityId/details',
    getFullUrl: function ({
      projectId,
      activityId,
      emissionId,
      approvalId,
      subCategory,
    }: {
      projectId: string;
      activityId: string;
      emissionId: string;
      approvalId: string;
      subCategory: EmissionSourceSubCategory;
    }) {
      return appendQueryString(compile(this.url)({ projectId, activityId }), { emissionId, approvalId, subCategory });
    },
  },
  [Route.ORG_CFV_PROJECT_DATA_COLLECTION]: {
    title: (t: NextTFunction) => t('sidebar.dataCollection'),
    roles: [...PROJECT_ADMIN_ROLES, Role.DEPARTMENT_MANAGER],
    description: (t: NextTFunction) => t('common.infoBoxResultiOActivityDataCollection'),
    permissions: undefined,
    url: '/org-cfv/:projectId/data-collection',
    getFullUrl: function ({
      projectId,
      category,
      subCategory,
      emissionGroupId,
      emissionId,
    }: {
      projectId: string;
      category?: EmissionSourceCategory;
      subCategory?: EmissionSourceSubCategory;
      emissionGroupId?: string;
      emissionId?: string;
    }) {
      return appendQueryString(compile(this.url)({ projectId }), {
        category,
        subCategory,
        emissionGroupId,
        emissionId,
      });
    },
  },
  [Route.ORG_CFV_PROJECT_DATA_COLLECTION_DETAILS]: {
    title: (t: NextTFunction) => t('org-cfv-data-entry.activityDataDetails'),
    roles: [...PROJECT_ADMIN_ROLES, Role.DEPARTMENT_MANAGER],
    permissions: undefined,
    url: '/org-cfv/:projectId/data-collection/:activityId/details',
    getFullUrl: function ({
      projectId,
      activityId,
      emissionId,
      approvalId,
      subCategory,
    }: {
      projectId: string;
      activityId: string;
      emissionId: string;
      approvalId?: string;
      subCategory: EmissionSourceSubCategory;
    }) {
      return appendQueryString(compile(this.url)({ projectId, activityId }), { emissionId, approvalId, subCategory });
    },
  },
  [Route.ORG_CFV_DATA_QUALITY_MGMT]: {
    title: (t: NextTFunction) => t('sidebar.dataQualityManagement'),
    roles: PROJECT_ADMIN_ROLES,
    description: (t: NextTFunction) => t('common.infoBoxResultiODataQualityManagement'),
    permissions: undefined,
    url: '/org-cfv/:projectId/data-quality-management',
    getFullUrl: function ({ projectId }: { projectId: string }) {
      return appendQueryString(compile(this.url)({ projectId }));
    },
  },
  [Route.ORG_CFV_DATA_QUALITY_MGMT_DESCRIPTION]: {
    title: (t: NextTFunction) => t('org-cfv-list-create-project.dataQualityMgmtIntroduction'),
    roles: PROJECT_ADMIN_ROLES,
    permissions: undefined,
    url: '/org-cfv/:projectId/data-quality-management/description',
    getFullUrl: function ({ projectId }: { projectId: string }) {
      return appendQueryString(compile(this.url)({ projectId }));
    },
  },
  [Route.ORG_CFV_UNCERTAINTY_QUANTITATIVE_ASSESSMENT]: {
    title: (t: NextTFunction) => t('sidebar.uncertaintyQuantitativeAssessment'),
    roles: PROJECT_ADMIN_ROLES,
    description: (t: NextTFunction) => t('common.infoBoxResultiOUncertainty'),
    permissions: undefined,
    url: '/org-cfv/:projectId/uncertainty-quantitative-assessment',
    getFullUrl: function ({ projectId }: { projectId: string }) {
      return appendQueryString(compile(this.url)({ projectId }));
    },
  },
  [Route.ORG_CFV_DASHBOARD]: {
    title: (t: NextTFunction) => t('sidebar.dashboard'),
    roles: PROJECT_ADMIN_ROLES,
    description: (t: NextTFunction) => t('common.orgDashboardInfo'),
    permissions: undefined,
    url: '/org-cfv/:projectId/dashboard',
    getFullUrl: function ({ projectId }: { projectId: string }) {
      return appendQueryString(compile(this.url)({ projectId }));
    },
  },
  [Route.SINGLE_BOUNDARY_REPORT_EXPORT]: {
    title: (t: NextTFunction) => t('org-cfv-dashboard.projectReport'),
    roles: PROJECT_ADMIN_ROLES,
    description: (t: NextTFunction) => t('common.infoBoxReportiOProjectReport'),
    permissions: undefined,
    url: '/org-cfv/report',
    getFullUrl: function ({ page, limit }: { page?: number; limit?: number } = {}) {
      return appendQueryString(this.url, { page, limit });
    },
  },
  [Route.REPORT_ORG_CFV_SINGLE_REPORT_EDITOR]: {
    title: (t: NextTFunction) => t('common.edit'),
    roles: PROJECT_ADMIN_ROLES,
    permissions: undefined,
    url: '/org-cfv/report/edit',
    getFullUrl: function ({
      templateId,
      isDefault,
      isCopy,
    }: {
      templateId: string;
      isDefault: boolean;
      isCopy: boolean;
    }) {
      return appendQueryString(this.url, { templateId, isDefault: isDefault.toString(), isCopy: isCopy.toString() });
    },
  },
  [Route.REPORT_ORG_CFV_SINGLE_REPORT_EDITOR_VIEW]: {
    title: (t: NextTFunction) => t('common.view'),
    roles: PROJECT_ADMIN_ROLES,
    permissions: undefined,
    url: '/org-cfv/report/view',
    getFullUrl: function ({ templateId, isDefault }: { templateId: string; isDefault: boolean }) {
      return appendQueryString(this.url, { templateId, isDefault: isDefault.toString() });
    },
  },
};

const PRODUCT_CFV_ROUTE_CONFIGS = {
  [Route.PRODUCT_CFV]: {
    title: (t: NextTFunction) => t('sidebar.productCFV'),
    roles: [...PROJECT_ADMIN_ROLES, Role.DEPARTMENT_MANAGER, Role.DEPARTMENT_USER, Role.DATA_COLLECTOR],
    permissions: undefined,
    url: '/product-cfv',
    getFullUrl: function (query?: { tab?: 'whole' | 'single' }) {
      return appendQueryString(this.url, { tab: query?.tab || 'whole' });
    },
  },
  [Route.PRODUCT_CFV_SUPPORTING_DOCUMENT]: {
    title: (t: NextTFunction) => t('common.supportingDocument'),
    roles: [...PROJECT_ADMIN_ROLES, Role.DEPARTMENT_MANAGER, Role.DEPARTMENT_USER, Role.DATA_COLLECTOR],
    description: (t: NextTFunction) => t('common.infoBoxCollectDataiPSupportingDocument'),
    permissions: undefined,
    url: '/product-cfv/supporting-document',
    getFullUrl: function ({ projectId }: { projectId: string }) {
      return appendQueryString(this.url, { projectId });
    },
  },
  [Route.PRODUCT_CFV_ENERGY_RESOURCES]: {
    title: (t: NextTFunction) => t('cfp-project.energyResources'),
    roles: [...PROJECT_ADMIN_ROLES, Role.DEPARTMENT_MANAGER, Role.DEPARTMENT_USER, Role.DATA_COLLECTOR],
    description: (t: NextTFunction) => t('common.infoBoxCollectDataiPEnergyAndResources'),
    permissions: undefined,
    url: '/product-cfv/energy-resources',
    getFullUrl: function ({ projectId }: { projectId: string }) {
      return appendQueryString(this.url, { projectId });
    },
  },
  [Route.PRODUCT_CFV_DATA_ENTRY]: {
    title: (t: NextTFunction) => t('sidebar.activityDataEntry'),
    roles: [...PROJECT_ADMIN_ROLES, Role.DEPARTMENT_MANAGER, Role.DEPARTMENT_USER, Role.DATA_COLLECTOR],
    description: (t: NextTFunction) => t('common.infoBoxCollectDataiPActivityDataEntry'),
    permissions: undefined,
    url: '/product-cfv/data-entry',
    getFullUrl: function ({ projectId, type }: { projectId: string; type?: DataEntryTabType }) {
      return appendQueryString(this.url, { projectId, type });
    },
  },
  [Route.PRODUCT_CFV_DATA_ENTRY_BULK]: {
    title: (t: NextTFunction) => t('bulk-input.bulkInput'),
    roles: [...PROJECT_ADMIN_ROLES, Role.DEPARTMENT_MANAGER, Role.DEPARTMENT_USER, Role.DATA_COLLECTOR],
    permissions: undefined,
    url: '/product-cfv/data-entry/bulk-input',
    getFullUrl: function ({
      projectId,
      approvalId,
      type,
    }: {
      projectId: string;
      approvalId?: string;
      type?: DataEntryTabType;
    }) {
      return appendQueryString(this.url, { projectId, approvalId, type });
    },
  },

  [Route.PRODUCT_CFV_DATA_ENTRY_DETAIL]: {
    title: (t: NextTFunction) => t('org-cfv-data-entry.activityDataDetails'),
    roles: [...PROJECT_ADMIN_ROLES, Role.DEPARTMENT_MANAGER, Role.DEPARTMENT_USER, Role.DATA_COLLECTOR],
    permissions: undefined,
    url: '/product-cfv/data-entry/detail',
    getFullUrl: function ({
      projectId,
      type,
      approvalId,
    }: {
      projectId: string;
      type: DataEntryTabType;
      approvalId: string;
    }) {
      return appendQueryString(this.url, { projectId, type, approvalId });
    },
  },
  [Route.PRODUCT_CFV_DATA_APPROVAL]: {
    title: (t: NextTFunction) => t('sidebar.activityDataApproval'),
    roles: [...PROJECT_ADMIN_ROLES, Role.DEPARTMENT_MANAGER],
    description: (t: NextTFunction) => t('common.infoBoxDataApprovaliPActivityDataApproval'),
    permissions: undefined,
    url: '/product-cfv/data-approval',
    getFullUrl: function ({ projectId, type }: { projectId: string; type?: DataEntryTabType }) {
      return appendQueryString(this.url, { projectId, type });
    },
  },
  [Route.PRODUCT_CFV_DATA_APPROVAL_DETAIL]: {
    title: (t: NextTFunction) => t('org-cfv-data-entry.activityDataDetails'),
    roles: [...PROJECT_ADMIN_ROLES, Role.DEPARTMENT_MANAGER],
    permissions: undefined,
    url: '/product-cfv/data-approval/detail',
    getFullUrl: function ({
      projectId,
      type,
      approvalId,
    }: {
      projectId: string;
      type: DataEntryTabType;
      approvalId: string;
    }) {
      return appendQueryString(this.url, { projectId, type, approvalId });
    },
  },
  [Route.PRODUCT_CFV_DATA_COLLECTION]: {
    title: (t: NextTFunction) => t('sidebar.dataCollection'),
    roles: [...PROJECT_ADMIN_ROLES, Role.DEPARTMENT_MANAGER],
    description: (t: NextTFunction) => t('common.infoBoxResultiPActivityDataCollection'),
    permissions: undefined,
    url: '/product-cfv/data-collection',
    getFullUrl: function ({ projectId }: { projectId: string }) {
      return appendQueryString(this.url, { projectId });
    },
  },
  [Route.PRODUCT_CFV_DATA_COLLECTION_DETAIL]: {
    title: (t: NextTFunction) => t('org-cfv-data-entry.activityDataDetails'),
    roles: [...PROJECT_ADMIN_ROLES, Role.DEPARTMENT_MANAGER],
    permissions: undefined,
    url: '/product-cfv/data-collection/detail',
    getFullUrl: function ({
      projectId,
      productSeriesId,
      refProductSeriesId,
    }: {
      projectId: string;
      productSeriesId: string;
      refProductSeriesId: string;
    }) {
      return appendQueryString(this.url, { projectId, productSeriesId, refProductSeriesId });
    },
  },
  [Route.PRODUCT_CFV_DATA_COLLECTION_RAW_MATERIAL_APPROVAL]: {
    title: (t: NextTFunction) => t('org-cfv-data-entry.activityDataDetails'),
    roles: [...PROJECT_ADMIN_ROLES, Role.DEPARTMENT_MANAGER],
    permissions: undefined,
    url: '/product-cfv/data-collection/raw-material',
    getFullUrl: function ({ projectId, approvalId }: { projectId: string; approvalId: string }) {
      return appendQueryString(this.url, { projectId, approvalId });
    },
  },
  [Route.PRODUCT_CFV_DATA_COLLECTION_MANUFACTURING_APPROVAL]: {
    title: (t: NextTFunction) => t('org-cfv-data-entry.activityDataDetails'),
    roles: [...PROJECT_ADMIN_ROLES, Role.DEPARTMENT_MANAGER],
    permissions: undefined,
    url: '/product-cfv/data-collection/product-manufacturing',
    getFullUrl: function ({ projectId, approvalId }: { projectId: string; approvalId: string }) {
      return appendQueryString(this.url, { projectId, approvalId });
    },
  },
  [Route.PRODUCT_CFV_DATA_COLLECTION_SALES_INFORMATION_APPROVAL]: {
    title: (t: NextTFunction) => t('org-cfv-data-entry.activityDataDetails'),
    roles: [...PROJECT_ADMIN_ROLES, Role.DEPARTMENT_MANAGER],
    permissions: undefined,
    url: '/product-cfv/data-collection/sales-information',
    getFullUrl: function ({ projectId, approvalId }: { projectId: string; approvalId: string }) {
      return appendQueryString(this.url, { projectId, approvalId });
    },
  },
  [Route.PRODUCT_CFV_ENERGY_RESOURCES_RESULT]: {
    title: (t: NextTFunction) => t('common.energyResourcesResult'),
    roles: [...PROJECT_ADMIN_ROLES, Role.DEPARTMENT_MANAGER],
    description: (t: NextTFunction) => t('common.infoBoxResultiPEnergyAndResourcesResult'),
    permissions: undefined,
    url: '/product-cfv/energy-resources-result',
    getFullUrl: function ({ projectId }: { projectId: string }) {
      return appendQueryString(this.url, { projectId });
    },
  },
  [Route.PRODUCT_CFV_ENERGY_RESOURCES_RESULT_DETAIL]: {
    title: (t: NextTFunction) => t('common.energyAndResourcesResultDetails'),
    roles: [...PROJECT_ADMIN_ROLES, Role.DEPARTMENT_MANAGER],
    permissions: undefined,
    url: '/product-cfv/energy-resources-result/details',
    getFullUrl: function ({
      projectId,
      collectionId,
      refProductSeriesId,
    }: {
      projectId: string;
      collectionId: string;
      refProductSeriesId: string;
    }) {
      return appendQueryString(this.url, { projectId, collectionId, refProductSeriesId });
    },
  },
  [Route.PRODUCT_CFV_DASHBOARD]: {
    title: (t: NextTFunction) => t('sidebar.dashboard'),
    roles: [...PROJECT_ADMIN_ROLES, Role.DEPARTMENT_MANAGER],
    description: (t: NextTFunction) => t('common.cfpDashboardInfo'),
    permissions: undefined,
    url: '/product-cfv/dashboard',
    getFullUrl: function ({ projectId }: { projectId: string }) {
      return appendQueryString(this.url, { projectId });
    },
  },
  [Route.PRODUCT_CFV_REPORTING]: {
    title: (t: NextTFunction) => t('common.reportCenter'),
    roles: [...PROJECT_ADMIN_ROLES, Role.DEPARTMENT_MANAGER],
    description: (t: NextTFunction) => t('common.infoBoxReportiPProjectReport'),
    permissions: undefined,
    url: '/product-cfv/reporting',
    getFullUrl: function ({ projectId }: { projectId: string }) {
      return appendQueryString(this.url, { projectId });
    },
  },
  [Route.PRODUCT_CFV_REPORTING_EDIT]: {
    title: (t: NextTFunction) => t('common.editReport'),
    roles: PROJECT_ADMIN_ROLES,
    permissions: undefined,
    url: '/product-cfv/reporting/edit',
    getFullUrl: function ({ projectId, templateId }: { projectId: string; templateId?: string }) {
      return appendQueryString(this.url, { projectId, templateId });
    },
  },
  [Route.PRODUCT_CFV_REPORTING_PREVIEW]: {
    title: (t: NextTFunction) => t('common.reportPreview'),
    roles: [...PROJECT_ADMIN_ROLES, Role.DEPARTMENT_MANAGER],
    permissions: undefined,
    url: '/product-cfv/reporting/preview',
    getFullUrl: function ({ projectId, templateId }: { projectId: string; templateId?: string }) {
      return appendQueryString(this.url, { projectId, templateId });
    },
  },
  [Route.PRODUCT_CFV_DATA_QUALITY]: {
    title: (t: NextTFunction) => t('sidebar.dataQualityManagement'),
    roles: [...PROJECT_ADMIN_ROLES, Role.DEPARTMENT_MANAGER],
    description: (t: NextTFunction) => t('common.infoBoxResultiPDataQualityManagement'),
    permissions: undefined,
    url: '/product-cfv/data-quality',
    getFullUrl: function ({ projectId }: { projectId: string }) {
      return appendQueryString(this.url, { projectId });
    },
  },
  [Route.PRODUCT_CFV_DATA_QUALITY_DETAIL]: {
    title: (t: NextTFunction) => t('org-cfv-data-quality-management.dataQualityDetail'),
    roles: [...PROJECT_ADMIN_ROLES, Role.DEPARTMENT_MANAGER],
    permissions: undefined,
    url: '/product-cfv/data-quality/detail',
    getFullUrl: function ({
      projectId,
      productSeriesId,
      refProductSeriesId,
    }: {
      projectId: string;
      productSeriesId: string;
      refProductSeriesId: string;
    }) {
      return appendQueryString(this.url, { projectId, productSeriesId, refProductSeriesId });
    },
  },
  [Route.PRODUCT_CFV_SENSITIVITY_ANALYSIS]: {
    title: (t: NextTFunction) => t('common.sensitivityAnalysesAndUncertaintyAssessments'),
    roles: [...PROJECT_ADMIN_ROLES, Role.DEPARTMENT_MANAGER],
    description: (t: NextTFunction) => t('common.infoBoxResultiPSensitivityAndUncertainty'),
    permissions: undefined,
    url: '/product-cfv/sensitivity-analysis',
    getFullUrl: function ({ projectId }: { projectId: string }) {
      return appendQueryString(this.url, { projectId });
    },
  },
  [Route.PRODUCT_CFV_SENSITIVITY_ANALYSIS_DETAIL]: {
    title: (t: NextTFunction) => t('common.sensitivityAndUncertaintyDetails'),
    roles: [...PROJECT_ADMIN_ROLES, Role.DEPARTMENT_MANAGER],
    permissions: undefined,
    url: '/product-cfv/sensitivity-analysis/detail',
    getFullUrl: function ({
      projectId,
      refProductSeriesId,
      productSeriesId,
    }: {
      projectId: string;
      refProductSeriesId: string;
      productSeriesId: string;
    }) {
      return appendQueryString(this.url, { projectId, refProductSeriesId, productSeriesId });
    },
  },
  [Route.PRODUCT_CFV_EDIT_SENSITIVITY_ANALYSIS]: {
    title: (t: NextTFunction) => t('common.editUncertaintyAssessments'),
    roles: PROJECT_ADMIN_ROLES,
    permissions: undefined,
    url: '/product-cfv/sensitivity-analysis/detail/edit',
    getFullUrl: function ({
      projectId,
      refProductSeriesId,
      productSeriesId,
    }: {
      projectId: string;
      refProductSeriesId: string;
      productSeriesId: string;
    }) {
      return appendQueryString(this.url, { projectId, refProductSeriesId, productSeriesId });
    },
  },
};

const PROJECT_FLOW_ROUTE_CONFIGS = {
  ...ORG_CFV_ROUTE_CONFIGS,
  ...PRODUCT_CFV_ROUTE_CONFIGS,
};

export const ROUTE_CONFIG = {
  [Route.ROOT]: {
    title: (t: NextTFunction) => t('common.homepage'),
    roles: [...PROJECT_ADMIN_ROLES, Role.DEPARTMENT_MANAGER, Role.DEPARTMENT_USER, Role.DATA_COLLECTOR],
    permissions: undefined,
    url: '',
    getFullUrl: function () {
      return appendQueryString(this.url);
    },
  },

  [Route.HOME]: {
    title: (t: NextTFunction) => t('common.home'),
    roles: [...PROJECT_ADMIN_ROLES, Role.DEPARTMENT_MANAGER, Role.DEPARTMENT_USER, Role.DATA_COLLECTOR],
    permissions: undefined,
    url: '/home',
    getFullUrl: function () {
      return appendQueryString(this.url);
    },
  },

  [Route.LOGIN]: {
    title: (t: NextTFunction) => t('account-management.login'),
    roles: undefined,
    permissions: undefined,
    url: '/login',
    getFullUrl: function () {
      return appendQueryString(this.url, {}, false);
    },
  },
  [Route.RESET_PASSWORD]: {
    title: (t: NextTFunction) => t('reset-password.setPassword'),
    roles: undefined,
    permissions: undefined,
    url: '/reset-password',
    getFullUrl: function () {
      return appendQueryString(this.url, {}, false);
    },
  },
  [Route.DOCUMENT_VIEWER]: {
    title: () => '',
    roles: [...PROJECT_ADMIN_ROLES, Role.DEPARTMENT_MANAGER, Role.DEPARTMENT_USER, Role.DATA_COLLECTOR],
    permissions: undefined,
    url: '/document-viewer',
    getFullUrl: function () {
      return appendQueryString(this.url);
    },
  },
  [Route.SELECT_COMPANY]: {
    title: (t: NextTFunction) => t('common.selectCompany'),
    roles: undefined,
    permissions: undefined,
    url: '/select-company',
    getFullUrl: function () {
      return appendQueryString(this.url, {}, false);
    },
  },
  [Route.SYSTEM_ERROR]: {
    title: (t: NextTFunction) => t('common.systemError'),
    roles: undefined,
    permissions: undefined,
    url: '/system-error',
    getFullUrl: function () {
      return appendQueryString(this.url);
    },
  },
  [Route.FORBIDDEN]: {
    title: () => '',
    roles: undefined,
    permissions: undefined,
    url: '/forbidden',
    getFullUrl: function () {
      return appendQueryString(this.url);
    },
  },
  [Route.ACCOUNT_MANAGEMENT]: {
    title: (t: NextTFunction) => t('sidebar.accountMgmt'),
    roles: [...PROJECT_ADMIN_ROLES, Role.DEPARTMENT_MANAGER],
    permissions: undefined,
    description: (t: NextTFunction) => t('common.infoBoxAccountPermissionMgmt'),
    url: '/account-management',
    getFullUrl: function ({ tab, page, limit }: { tab?: string; page?: number; limit?: number } = {}) {
      return appendQueryString(this.url, { tab, page, limit });
    },
  },
  [Route.ACCOUNT_MANAGEMENT_CREATE_ACCOUNT]: {
    title: (t: NextTFunction) => t('account-management.creatNewAccount'),
    roles: PROJECT_ADMIN_ROLES,
    permissions: undefined,
    url: '/account-management/create-account',
    getFullUrl: function ({ departmentId }: { departmentId?: string }) {
      return appendQueryString(this.url, { departmentId });
    },
  },
  [Route.ACCOUNT_MANAGEMENT_CREATE_DEPARTMENT]: {
    title: (t: NextTFunction) => t('account-management.createNewDept'),
    roles: PROJECT_ADMIN_ROLES,
    permissions: undefined,
    url: '/account-management/create-department',
    getFullUrl: function () {
      return appendQueryString(this.url);
    },
  },
  [Route.ACCOUNT_MANAGEMENT_EDIT_ACCOUNT]: {
    title: (t: NextTFunction) => t('account-management.editAccountInfo'),
    roles: PROJECT_ADMIN_ROLES,
    permissions: undefined,
    url: '/account-management/view-account/edit-account/:accountId',
    getFullUrl: function ({ accountId }: { accountId: string }) {
      return appendQueryString(compile(this.url)({ accountId }));
    },
  },
  [Route.ACCOUNT_MANAGEMENT_VIEW_ACCOUNT]: {
    title: (t: NextTFunction) => t('sidebar.accountInfo'),
    roles: PROJECT_ADMIN_ROLES,
    permissions: undefined,
    url: '/account-management/view-account/:accountId',
    getFullUrl: function ({ accountId }: { accountId: string }) {
      return appendQueryString(compile(this.url)({ accountId }));
    },
  },
  [Route.ACCOUNT_MANAGEMENT_EDIT_DEPARTMENT]: {
    title: (t: NextTFunction) => t('account-management.editDeptInfo'),
    roles: PROJECT_ADMIN_ROLES,
    permissions: undefined,
    url: '/account-management/view-account/edit-department/:departmentId',
    getFullUrl: function ({ departmentId }: { departmentId: string }) {
      return appendQueryString(compile(this.url)({ departmentId }));
    },
  },

  [Route.VENDOR]: {
    title: (t: NextTFunction) => t('sidebar.vendorMaster'),
    roles: [...PROJECT_ADMIN_ROLES, Role.DEPARTMENT_MANAGER, Role.DEPARTMENT_USER, Role.DATA_COLLECTOR],
    description: (t: NextTFunction) => t('common.infoBoxBDSupplierMaster'),
    permissions: [AssignedPermission.VENDOR],
    url: '/factory-database/vendor',
    getFullUrl: function () {
      return appendQueryString(this.url);
    },
  },
  [Route.CREATE_VENDOR_BULK]: {
    title: (t: NextTFunction) => t('bulk-input.bulkInput'),
    roles: [...PROJECT_ADMIN_ROLES, Role.DEPARTMENT_MANAGER, Role.DEPARTMENT_USER, Role.DATA_COLLECTOR],
    permissions: [AssignedPermission.VENDOR],
    url: '/factory-database/vendor/bulk-input',
    getFullUrl: function () {
      return appendQueryString(this.url);
    },
  },
  [Route.EDIT_VENDOR]: {
    title: (t: NextTFunction) => t('fd-raw-material.editVendor'),
    roles: [...PROJECT_ADMIN_ROLES, Role.DEPARTMENT_MANAGER, Role.DEPARTMENT_USER, Role.DATA_COLLECTOR],
    permissions: [AssignedPermission.VENDOR],
    url: '/factory-database/vendor/edit',
    getFullUrl: function ({ vendorId }: { vendorId: string }) {
      return appendQueryString(this.url, { id: vendorId });
    },
  },
  [Route.CREATE_VENDOR]: {
    title: (t: NextTFunction) => t('fd-raw-material.newVendor'),
    roles: [...PROJECT_ADMIN_ROLES, Role.DEPARTMENT_MANAGER, Role.DEPARTMENT_USER, Role.DATA_COLLECTOR],
    permissions: [AssignedPermission.VENDOR],
    url: '/factory-database/vendor/create',
    getFullUrl: function () {
      return appendQueryString(this.url);
    },
  },
  [Route.RAW_MATERIAL]: {
    title: (t: NextTFunction) => t('sidebar.rawMaterial'),
    roles: [...PROJECT_ADMIN_ROLES, Role.DEPARTMENT_MANAGER, Role.DEPARTMENT_USER, Role.DATA_COLLECTOR],
    description: (t: NextTFunction) => t('common.infoBoxBDRMInfo'),
    permissions: [AssignedPermission.RAW_MATERIAL],
    url: '/factory-database/raw-material',
    getFullUrl: function () {
      return appendQueryString(this.url);
    },
  },
  [Route.CREATE_RAW_MATERIAL]: {
    title: (t: NextTFunction) => t('fd-raw-material.newRawMaterial'),
    roles: [...PROJECT_ADMIN_ROLES, Role.DEPARTMENT_MANAGER, Role.DEPARTMENT_USER, Role.DATA_COLLECTOR],
    permissions: [AssignedPermission.RAW_MATERIAL],
    url: '/factory-database/raw-material/create',
    getFullUrl: function () {
      return appendQueryString(this.url);
    },
  },
  [Route.CREATE_RAW_MATERIAL_BULK]: {
    title: (t: NextTFunction) => t('bulk-input.bulkInput'),
    roles: [...PROJECT_ADMIN_ROLES, Role.DEPARTMENT_MANAGER, Role.DEPARTMENT_USER, Role.DATA_COLLECTOR],
    permissions: [AssignedPermission.RAW_MATERIAL],
    url: '/factory-database/raw-material/bulk-input',
    getFullUrl: function () {
      return appendQueryString(this.url);
    },
  },
  [Route.CREATE_RAW_MATERIAL_BULK_AI]: {
    title: (t: NextTFunction) => t('bulk-input.smartBulkInput'),
    roles: [...PROJECT_ADMIN_ROLES, Role.DEPARTMENT_MANAGER, Role.DEPARTMENT_USER, Role.DATA_COLLECTOR],
    permissions: [AssignedPermission.RAW_MATERIAL],
    url: '/factory-database/raw-material/ai-mapping-bulk-input',
    getFullUrl: function () {
      return appendQueryString(this.url);
    },
  },
  [Route.CREATE_RAW_MATERIAL_BULK_MULTI_LINGUAL]: {
    title: (t: NextTFunction) => t('bulk-input.bulkInput'),
    roles: [...PROJECT_ADMIN_ROLES, Role.DEPARTMENT_MANAGER, Role.DEPARTMENT_USER, Role.DATA_COLLECTOR],
    permissions: [AssignedPermission.RAW_MATERIAL],
    url: '/factory-database/raw-material/multi-lingual-bulk-input',
    getFullUrl: function () {
      return appendQueryString(this.url);
    },
  },
  [Route.EDIT_RAW_MATERIAL]: {
    title: (t: NextTFunction) => t('fd-raw-material.editRawMaterial'),
    roles: [...PROJECT_ADMIN_ROLES, Role.DEPARTMENT_MANAGER, Role.DEPARTMENT_USER, Role.DATA_COLLECTOR],
    permissions: [AssignedPermission.RAW_MATERIAL],
    url: '/factory-database/raw-material/details/edit',
    getFullUrl: function ({ rawMaterialId, scrollTo }: { rawMaterialId: string; scrollTo?: string }) {
      const queryParam: Record<string, string> = { rawMaterialId };
      if (scrollTo) {
        queryParam[SCROLL_TO_KEY] = 'true';
      }
      return appendQueryString(this.url, queryParam);
    },
  },
  [Route.RAW_MATERIAL_DETAIL]: {
    title: (t: NextTFunction) => t('common.rawMaterialDetails'),
    roles: [...PROJECT_ADMIN_ROLES, Role.DEPARTMENT_MANAGER, Role.DEPARTMENT_USER, Role.DATA_COLLECTOR],
    permissions: [AssignedPermission.RAW_MATERIAL],
    url: '/factory-database/raw-material/details',
    getFullUrl: function ({ rawMaterialId }: { rawMaterialId: string }) {
      const queryParam: Record<string, string> = { rawMaterialId };
      return appendQueryString(this.url, queryParam);
    },
  },
  [Route.EDIT_RAW_MATERIAL_BULK]: {
    title: (t: NextTFunction) => t('common.bulkEdit'),
    roles: [...PROJECT_ADMIN_ROLES, Role.DEPARTMENT_MANAGER, Role.DEPARTMENT_USER, Role.DATA_COLLECTOR],
    permissions: [AssignedPermission.RAW_MATERIAL],
    url: '/factory-database/raw-material/bulk-edit',
    getFullUrl: function ({
      selectAll,
      selectedIds,
      partNoFuzzyName,
      vendorFuzzyName,
      shipmentLocationFuzzyName,
      excludedIds,
    }: {
      selectAll: boolean;
      selectedIds: string[] | undefined;
      partNoFuzzyName: string | undefined;
      vendorFuzzyName: string | undefined;
      shipmentLocationFuzzyName: string | undefined;
      excludedIds: string[] | undefined;
    }) {
      const queryParam: Record<string, string> = {};
      queryParam.selectAll = selectAll ? 'true' : 'false';
      if (selectedIds) {
        queryParam.selectedIds = selectedIds.join(',');
      }
      if (partNoFuzzyName) {
        queryParam.partNoFuzzyName = partNoFuzzyName;
      }
      if (vendorFuzzyName) {
        queryParam.vendorFuzzyName = vendorFuzzyName;
      }
      if (shipmentLocationFuzzyName) {
        queryParam.shipmentLocationFuzzyName = shipmentLocationFuzzyName;
      }
      if (excludedIds) {
        queryParam.excludedIds = excludedIds.join(',');
      }
      return appendQueryString(this.url, queryParam);
    },
  },
  [Route.RAW_MATERIAL_TRANSPORTATION]: {
    title: (t: NextTFunction) => t('sidebar.rawMaterialTransportation'),
    roles: [...PROJECT_ADMIN_ROLES, Role.DEPARTMENT_MANAGER, Role.DEPARTMENT_USER, Role.DATA_COLLECTOR],
    description: (t: NextTFunction) => t('common.infoBoxBDTransportationRM'),
    permissions: [AssignedPermission.RAW_MATERIAL_TRANSPORTATION],
    url: '/factory-database/raw-material-transportation',
    getFullUrl: function () {
      return appendQueryString(this.url);
    },
  },
  [Route.CREATE_RAW_MATERIAL_TRANSPORTATION]: {
    title: (t: NextTFunction) => t('fd-rmt.rmtSetting'),
    roles: [...PROJECT_ADMIN_ROLES, Role.DEPARTMENT_MANAGER, Role.DEPARTMENT_USER, Role.DATA_COLLECTOR],
    permissions: [AssignedPermission.RAW_MATERIAL_TRANSPORTATION],
    url: '/factory-database/raw-material-transportation/create',
    getFullUrl: function ({ vendorCode, manufacturerCode }: { vendorCode: string; manufacturerCode: string }) {
      return appendQueryString(this.url, { vendorCode, manufacturerCode });
    },
  },
  [Route.EDIT_RAW_MATERIAL_TRANSPORTATION]: {
    title: (t: NextTFunction) => t('fd-rmt.rmtSetting'),
    roles: [...PROJECT_ADMIN_ROLES, Role.DEPARTMENT_MANAGER, Role.DEPARTMENT_USER, Role.DATA_COLLECTOR],
    permissions: [AssignedPermission.RAW_MATERIAL_TRANSPORTATION],
    url: '/factory-database/raw-material-transportation/edit',
    getFullUrl: function ({ transportationId, scrollTo }: { transportationId: string; scrollTo?: string }) {
      const queryParam: Record<string, string> = { transportationId };
      if (scrollTo) {
        queryParam[SCROLL_TO_KEY] = 'true';
      }
      return appendQueryString(this.url, queryParam);
    },
  },

  // Trial pages for supplier master
  [Route.VENDOR_MASTER_TRIAL]: {
    title: (t: NextTFunction) => t('sidebar.vendorMaster'),
    roles: [...PROJECT_ADMIN_ROLES, Role.DEPARTMENT_MANAGER, Role.DEPARTMENT_USER, Role.DATA_COLLECTOR],
    permissions: [AssignedPermission.VENDOR],
    url: '/factory-database/trial/suppliers',
    getFullUrl: function () {
      return appendQueryString(this.url);
    },
  },
  [Route.CREATE_VENDOR_MASTER_TRIAL]: {
    title: (t: NextTFunction) => t('common.add'),
    roles: [...PROJECT_ADMIN_ROLES, Role.DEPARTMENT_MANAGER, Role.DEPARTMENT_USER, Role.DATA_COLLECTOR],
    permissions: [AssignedPermission.VENDOR],
    url: '/factory-database/trial/suppliers/create',
    getFullUrl: function () {
      return appendQueryString(this.url);
    },
  },
  [Route.VIEW_VENDOR_MASTER_TRIAL]: {
    title: (t: NextTFunction) => t('common.view'),
    roles: [...PROJECT_ADMIN_ROLES, Role.DEPARTMENT_MANAGER, Role.DEPARTMENT_USER, Role.DATA_COLLECTOR],
    permissions: [AssignedPermission.VENDOR],
    url: '/factory-database/trial/suppliers/details',
    getFullUrl: function ({ id }: { id: string }) {
      return appendQueryString(this.url, { id });
    },
  },
  [Route.EDIT_VENDOR_MASTER_TRIAL]: {
    title: (t: NextTFunction) => t('common.edit'),
    roles: [...PROJECT_ADMIN_ROLES, Role.DEPARTMENT_MANAGER, Role.DEPARTMENT_USER, Role.DATA_COLLECTOR],
    permissions: [AssignedPermission.VENDOR],
    url: '/factory-database/trial/suppliers/edit',
    getFullUrl: function ({ id }: { id: string }) {
      return appendQueryString(this.url, { id });
    },
  },
  // Trial pages for raw material
  [Route.RAW_MATERIAL_TRANSPORTATION_TRIAL]: {
    title: (t: NextTFunction) => t('sidebar.rawMaterialTransportation'),
    roles: [...PROJECT_ADMIN_ROLES, Role.DEPARTMENT_MANAGER, Role.DEPARTMENT_USER, Role.DATA_COLLECTOR],
    permissions: [AssignedPermission.VENDOR],
    url: '/factory-database/trial/raw-material-transportation',
    getFullUrl: function () {
      return appendQueryString(this.url);
    },
  },
  [Route.CREATE_RAW_MATERIAL_TRANSPORTATION_TRIAL]: {
    title: (t: NextTFunction) => t('common.add'),
    roles: [...PROJECT_ADMIN_ROLES, Role.DEPARTMENT_MANAGER, Role.DEPARTMENT_USER, Role.DATA_COLLECTOR],
    permissions: [AssignedPermission.VENDOR],
    url: '/factory-database/trial/raw-material-transportation/create',
    getFullUrl: function () {
      return appendQueryString(this.url);
    },
  },
  [Route.CREATE_BULK_RAW_MATERIAL_TRANSPORTATION_TRIAL]: {
    title: (t: NextTFunction) => t('common.bulkInput'),
    roles: [...PROJECT_ADMIN_ROLES, Role.DEPARTMENT_MANAGER, Role.DEPARTMENT_USER, Role.DATA_COLLECTOR],
    permissions: [AssignedPermission.VENDOR],
    url: '/factory-database/trial/raw-material-transportation/create-bulk',
    getFullUrl: function () {
      return appendQueryString(this.url);
    },
  },
  [Route.VIEW_RAW_MATERIAL_TRANSPORTATION_TRIAL]: {
    title: (t: NextTFunction) => t('common.view'),
    roles: [...PROJECT_ADMIN_ROLES, Role.DEPARTMENT_MANAGER, Role.DEPARTMENT_USER, Role.DATA_COLLECTOR],
    permissions: [AssignedPermission.VENDOR],
    url: '/factory-database/trial/raw-material-transportation/details',
    getFullUrl: function ({ id }: { id: string }) {
      return appendQueryString(this.url, { id });
    },
  },
  [Route.EDIT_RAW_MATERIAL_TRANSPORTATION_TRIAL]: {
    title: (t: NextTFunction) => t('common.edit'),
    roles: [...PROJECT_ADMIN_ROLES, Role.DEPARTMENT_MANAGER, Role.DEPARTMENT_USER, Role.DATA_COLLECTOR],
    permissions: [AssignedPermission.VENDOR],
    url: '/factory-database/trial/raw-material-transportation/edit',
    getFullUrl: function ({ id }: { id: string }) {
      return appendQueryString(this.url, { id });
    },
  },
  [Route.WASTE_TRANSPORTATION]: {
    title: (t: NextTFunction) => t('sidebar.wasteTransportation'),
    roles: [...PROJECT_ADMIN_ROLES, Role.DEPARTMENT_MANAGER, Role.DEPARTMENT_USER, Role.DATA_COLLECTOR],
    description: (t: NextTFunction) => t('common.infoBoxBDTransportationOperationalWaste'),
    permissions: [AssignedPermission.WASTE_TRANSPORTATION],
    url: '/factory-database/waste-transportation',
    getFullUrl: function () {
      return appendQueryString(this.url);
    },
  },
  [Route.CREATE_WASTE_TRANSPORTATION]: {
    title: (t: NextTFunction) => t('wmc.newWmc'),
    roles: [...PROJECT_ADMIN_ROLES, Role.DEPARTMENT_MANAGER, Role.DEPARTMENT_USER, Role.DATA_COLLECTOR],
    permissions: [AssignedPermission.WASTE_TRANSPORTATION],
    url: '/factory-database/waste-transportation/create',
    getFullUrl: function () {
      return appendQueryString(this.url);
    },
  },
  [Route.EDIT_WASTE_TRANSPORTATION]: {
    title: (t: NextTFunction) => `${t('sidebar.wasteTransportation')} - ${t('common.edit')}`,
    roles: [...PROJECT_ADMIN_ROLES, Role.DEPARTMENT_MANAGER, Role.DEPARTMENT_USER, Role.DATA_COLLECTOR],
    permissions: [AssignedPermission.WASTE_TRANSPORTATION],
    url: '/factory-database/waste-transportation/edit',
    getFullUrl: function ({ transportationId, scrollTo }: { transportationId: string; scrollTo?: string }) {
      const queryParam: Record<string, string> = { transportationId };
      if (scrollTo) {
        queryParam[SCROLL_TO_KEY] = 'true';
      }
      return appendQueryString(this.url, queryParam);
    },
  },
  [Route.CREATE_WASTE_TRANSPORTATION_BULK]: {
    title: (t: NextTFunction) => t('bulk-input.bulkInput'),
    roles: [...PROJECT_ADMIN_ROLES, Role.DEPARTMENT_MANAGER, Role.DEPARTMENT_USER, Role.DATA_COLLECTOR],
    permissions: [AssignedPermission.WASTE_TRANSPORTATION],
    url: '/factory-database/waste-transportation/bulk-input',
    getFullUrl: function () {
      return appendQueryString(this.url);
    },
  },
  [Route.DISTRIBUTION_TRANSPORTATION]: {
    title: (t: NextTFunction) => t('sidebar.distributionTransportation'),
    roles: [...PROJECT_ADMIN_ROLES, Role.DEPARTMENT_MANAGER, Role.DEPARTMENT_USER, Role.DATA_COLLECTOR],
    description: (t: NextTFunction) => t('common.infoBoxBDTransportationDistribution'),
    permissions: [AssignedPermission.DISTRIBUTION_TRANSPORTATION],
    url: '/factory-database/distribution-transportation',
    getFullUrl: function () {
      return appendQueryString(this.url);
    },
  },
  [Route.CREATE_DISTRIBUTION_TRANSPORTATION]: {
    title: (t: NextTFunction) => t('fd-distribution-transportation.createCustomer'),
    roles: [...PROJECT_ADMIN_ROLES, Role.DEPARTMENT_MANAGER, Role.DEPARTMENT_USER, Role.DATA_COLLECTOR],
    permissions: [AssignedPermission.DISTRIBUTION_TRANSPORTATION],
    url: '/factory-database/distribution-transportation/create',
    getFullUrl: function () {
      return appendQueryString(this.url);
    },
  },
  [Route.CREATE_DISTRIBUTION_TRANSPORTATION_BULK]: {
    title: (t: NextTFunction) => t('bulk-input.bulkInput'),
    roles: [...PROJECT_ADMIN_ROLES, Role.DEPARTMENT_MANAGER, Role.DEPARTMENT_USER, Role.DATA_COLLECTOR],
    permissions: [AssignedPermission.DISTRIBUTION_TRANSPORTATION],
    url: '/factory-database/distribution-transportation/bulk-input',
    getFullUrl: function () {
      return appendQueryString(this.url);
    },
  },
  [Route.EDIT_DISTRIBUTION_TRANSPORTATION]: {
    title: (t: NextTFunction) => t('fd-distribution-transportation.editCustomerTransportation'),
    roles: [...PROJECT_ADMIN_ROLES, Role.DEPARTMENT_MANAGER, Role.DEPARTMENT_USER, Role.DATA_COLLECTOR],
    permissions: [AssignedPermission.DISTRIBUTION_TRANSPORTATION],
    url: '/factory-database/distribution-transportation/edit',
    getFullUrl: function ({ transportationId, scrollTo }: { transportationId: string; scrollTo?: string }) {
      const queryParam: Record<string, string> = { transportationId };
      if (scrollTo) {
        queryParam[SCROLL_TO_KEY] = 'true';
      }
      return appendQueryString(this.url, queryParam);
    },
  },
  [Route.PRODUCT_LINE]: {
    title: (t: NextTFunction) => t('sidebar.productBasicInfo'),
    roles: [...PROJECT_ADMIN_ROLES, Role.DEPARTMENT_MANAGER, Role.DEPARTMENT_USER, Role.DATA_COLLECTOR],
    description: (t: NextTFunction) => t('common.infoBoxBDProductInfo'),
    permissions: [AssignedPermission.PRODUCT_LINE],
    url: '/factory-database/product-line',
    getFullUrl: function () {
      return appendQueryString(this.url);
    },
  },
  [Route.CREATE_PRODUCT_LINE]: {
    title: (t: NextTFunction) => `${t('fd-product-line.productLine')} - ${t('common.create')}`,
    roles: [...PROJECT_ADMIN_ROLES, Role.DEPARTMENT_MANAGER, Role.DEPARTMENT_USER, Role.DATA_COLLECTOR],
    permissions: [AssignedPermission.PRODUCT_LINE],
    url: '/factory-database/product-line/create',
    getFullUrl: function () {
      return appendQueryString(this.url);
    },
  },
  [Route.CREATE_PRODUCT_LINE_BULK]: {
    title: (t: NextTFunction) => t('bulk-input.bulkInput'),
    roles: [...PROJECT_ADMIN_ROLES, Role.DEPARTMENT_MANAGER, Role.DEPARTMENT_USER, Role.DATA_COLLECTOR],
    permissions: [AssignedPermission.PRODUCT_LINE],
    url: '/factory-database/product-line/bulk-input',
    getFullUrl: function () {
      return appendQueryString(this.url);
    },
  },
  [Route.EDIT_PRODUCT_LINE]: {
    title: (t: NextTFunction) => `${t('fd-product-line.productLine')} - ${t('common.edit')}`,
    roles: [...PROJECT_ADMIN_ROLES, Role.DEPARTMENT_MANAGER, Role.DEPARTMENT_USER, Role.DATA_COLLECTOR],
    permissions: [AssignedPermission.PRODUCT_LINE],
    url: '/factory-database/product-line/edit',
    getFullUrl: function ({ productLineId }: { productLineId: string }) {
      return appendQueryString(this.url, { id: productLineId });
    },
  },
  [Route.CREATE_PRODUCT_SERIES]: {
    title: (t: NextTFunction) => `${t('fd-product-line.productSeries')} - ${t('common.create')}`,
    roles: [...PROJECT_ADMIN_ROLES, Role.DEPARTMENT_MANAGER, Role.DEPARTMENT_USER, Role.DATA_COLLECTOR],
    permissions: [AssignedPermission.PRODUCT_LINE],
    url: '/factory-database/product-line/product-series/create',
    getFullUrl: function ({ productLineId }: { productLineId: string }) {
      return appendQueryString(this.url, { productLineId });
    },
  },
  [Route.CREATE_PRODUCT_SERIES_BULK]: {
    title: (t: NextTFunction) => t('bulk-input.bulkInput'),
    roles: [...PROJECT_ADMIN_ROLES, Role.DEPARTMENT_MANAGER, Role.DEPARTMENT_USER, Role.DATA_COLLECTOR],
    permissions: [AssignedPermission.PRODUCT_LINE],
    url: '/factory-database/product-line/product-series/bulk-input',
    getFullUrl: function ({ productLineId }: { productLineId: string }) {
      return appendQueryString(this.url, { productLineId });
    },
  },
  [Route.EDIT_PRODUCT_SERIES]: {
    title: (t: NextTFunction) => `${t('fd-product-line.productSeries')} - ${t('common.edit')}`,
    roles: [...PROJECT_ADMIN_ROLES, Role.DEPARTMENT_MANAGER, Role.DEPARTMENT_USER, Role.DATA_COLLECTOR],
    permissions: [AssignedPermission.PRODUCT_LINE],
    url: '/factory-database/product-line/product-series/edit',
    getFullUrl: function ({ productLineId, productSeriesId }: { productLineId: string; productSeriesId: string }) {
      return appendQueryString(this.url, { productLineId, productSeriesId });
    },
  },
  [Route.PRODUCT_MATERIALS]: {
    title: (t: NextTFunction) => `${t('fd-product-line.productMaterialCode')}`,
    roles: [...PROJECT_ADMIN_ROLES, Role.DEPARTMENT_MANAGER, Role.DEPARTMENT_USER, Role.DATA_COLLECTOR],
    permissions: [AssignedPermission.PRODUCT_LINE],
    url: '/factory-database/product-line/product-materials',
    getFullUrl: function ({ productLineId, productSeriesId }: { productLineId: string; productSeriesId: string }) {
      return appendQueryString(this.url, { productLineId, productSeriesId });
    },
  },
  [Route.CREATE_PRODUCT_MATERIALS_BULK]: {
    title: (t: NextTFunction) => `${t('bulk-input.bulkInput')}`,
    roles: [...PROJECT_ADMIN_ROLES, Role.DEPARTMENT_MANAGER, Role.DEPARTMENT_USER, Role.DATA_COLLECTOR],
    permissions: [AssignedPermission.PRODUCT_LINE],
    url: '/factory-database/product-line/product-materials/bulk-input',
    getFullUrl: function ({ productLineId, productSeriesId }: { productLineId: string; productSeriesId: string }) {
      return appendQueryString(this.url, { productLineId, productSeriesId });
    },
  },
  [Route.SCENARIOS_CONFIGURATION]: {
    title: (t: NextTFunction) => t('sidebar.scenariosConfiguration'),
    roles: [...PROJECT_ADMIN_ROLES, Role.DEPARTMENT_MANAGER, Role.DEPARTMENT_USER, Role.DATA_COLLECTOR],
    description: (t: NextTFunction) => t('common.infoBoxBDScenariosConfiguration'),
    permissions: [AssignedPermission.SCENARIOS_CONFIGURATION],
    url: '/factory-database/scenarios-configuration',
    getFullUrl: function () {
      return appendQueryString(this.url);
    },
  },
  [Route.USAGE_SCENARIOS]: {
    title: (t: NextTFunction) => t('fd-scenarios.configureScenario'),
    roles: [...PROJECT_ADMIN_ROLES, Role.DEPARTMENT_MANAGER, Role.DEPARTMENT_USER, Role.DATA_COLLECTOR],
    permissions: [AssignedPermission.SCENARIOS_CONFIGURATION],
    url: '/factory-database/scenarios-configuration/usage-scenarios',
    getFullUrl: function ({
      productLineId,
      productSeriesId,
      scenarioConfigurationType = ScenarioConfigurationType.USAGE,
    }: {
      productLineId: string;
      productSeriesId: string;
      scenarioConfigurationType?: ScenarioConfigurationType;
    }) {
      return appendQueryString(this.url, { productLineId, productSeriesId, scenarioConfigurationType });
    },
  },
  [Route.CREATE_USAGE_SCENARIOS]: {
    title: (t: NextTFunction) => t('fd-scenarios.addScenario'),
    roles: [...PROJECT_ADMIN_ROLES, Role.DEPARTMENT_MANAGER, Role.DEPARTMENT_USER, Role.DATA_COLLECTOR],
    permissions: [AssignedPermission.SCENARIOS_CONFIGURATION],
    url: '/factory-database/scenarios-configuration/usage-scenarios/create',
    getFullUrl: function ({
      productLineId,
      productSeriesId,
      scenarioCountryAreaId,
    }: {
      productLineId: string;
      productSeriesId: string;
      scenarioCountryAreaId: string;
    }) {
      return appendQueryString(this.url, { productLineId, productSeriesId, scenarioCountryAreaId });
    },
  },
  [Route.EDIT_USAGE_SCENARIOS]: {
    title: (t: NextTFunction) => t('fd-scenarios.editScenario'),
    roles: [...PROJECT_ADMIN_ROLES, Role.DEPARTMENT_MANAGER, Role.DEPARTMENT_USER, Role.DATA_COLLECTOR],
    permissions: [AssignedPermission.SCENARIOS_CONFIGURATION],
    url: '/factory-database/scenarios-configuration/usage-scenarios/detail/edit',
    getFullUrl: function ({
      productLineId,
      productSeriesId,
      scenarioCountryAreaId,
      scenarioId,
    }: {
      productLineId: string;
      productSeriesId: string;
      scenarioCountryAreaId: string;
      scenarioId: string;
    }) {
      return appendQueryString(this.url, { productLineId, productSeriesId, scenarioCountryAreaId, scenarioId });
    },
  },
  [Route.USAGE_SCENARIOS_DETAIL]: {
    title: (t: NextTFunction) => t('fd-scenarios.scenarioDetail'),
    roles: [...PROJECT_ADMIN_ROLES, Role.DEPARTMENT_MANAGER, Role.DEPARTMENT_USER, Role.DATA_COLLECTOR],
    permissions: [AssignedPermission.SCENARIOS_CONFIGURATION],
    url: '/factory-database/scenarios-configuration/usage-scenarios/detail',
    getFullUrl: function ({
      productLineId,
      productSeriesId,
      scenarioCountryAreaId,
      scenarioId,
    }: {
      productLineId: string;
      productSeriesId: string;
      scenarioCountryAreaId: string;
      scenarioId: string;
    }) {
      return appendQueryString(this.url, { productLineId, productSeriesId, scenarioCountryAreaId, scenarioId });
    },
  },
  [Route.END_OF_LIFE_SCENARIOS_CREATE]: {
    title: (t: NextTFunction) => t('fd-scenarios.addScenario'),
    roles: [...PROJECT_ADMIN_ROLES, Role.DEPARTMENT_MANAGER, Role.DEPARTMENT_USER, Role.DATA_COLLECTOR],
    permissions: [AssignedPermission.SCENARIOS_CONFIGURATION],
    url: '/factory-database/scenarios-configuration/end-of-life-scenarios/create',
    getFullUrl: function ({
      productLineId,
      productSeriesId,
      scenarioCountryAreaId,
    }: {
      productLineId: string;
      productSeriesId: string;
      scenarioCountryAreaId: string;
    }) {
      return appendQueryString(this.url, { productLineId, productSeriesId, scenarioCountryAreaId });
    },
  },
  [Route.END_OF_LIFE_SCENARIOS_EDIT]: {
    title: (t: NextTFunction) => t('fd-scenarios.editScenario'),
    roles: [...PROJECT_ADMIN_ROLES, Role.DEPARTMENT_MANAGER, Role.DEPARTMENT_USER, Role.DATA_COLLECTOR],
    permissions: [AssignedPermission.SCENARIOS_CONFIGURATION],
    url: '/factory-database/scenarios-configuration/end-of-life-scenarios/detail/edit',
    getFullUrl: function ({
      productLineId,
      productSeriesId,
      scenarioCountryAreaId,
      scenarioId,
    }: {
      productLineId: string;
      productSeriesId: string;
      scenarioCountryAreaId: string;
      scenarioId: string;
    }) {
      return appendQueryString(this.url, { productLineId, productSeriesId, scenarioCountryAreaId, scenarioId });
    },
  },
  [Route.END_OF_LIFE_SCENARIOS_DETAIL]: {
    title: (t: NextTFunction) => t('fd-scenarios.scenarioDetail'),
    roles: [...PROJECT_ADMIN_ROLES, Role.DEPARTMENT_MANAGER, Role.DEPARTMENT_USER, Role.DATA_COLLECTOR],
    permissions: [AssignedPermission.SCENARIOS_CONFIGURATION],
    url: '/factory-database/scenarios-configuration/end-of-life-scenarios/detail',
    getFullUrl: function ({
      productLineId,
      productSeriesId,
      scenarioCountryAreaId,
      scenarioId,
    }: {
      productLineId: string;
      productSeriesId: string;
      scenarioCountryAreaId: string;
      scenarioId: string;
    }) {
      return appendQueryString(this.url, { productLineId, productSeriesId, scenarioCountryAreaId, scenarioId });
    },
  },
  [Route.EMPLOYEE]: {
    title: (t: NextTFunction) => t('common.employeeManagement'),
    roles: [...PROJECT_ADMIN_ROLES, Role.DEPARTMENT_MANAGER, Role.DEPARTMENT_USER, Role.DATA_COLLECTOR],
    description: (t: NextTFunction) => t('common.infoBoxBDEmployeeManagement'),
    permissions: [AssignedPermission.EMPLOYEE],
    url: '/factory-database/employee',
    getFullUrl: function () {
      return appendQueryString(this.url);
    },
  },
  [Route.EMPLOYEE_BULK]: {
    title: (t: NextTFunction) => t('bulk-input.bulkInput'),
    roles: [...PROJECT_ADMIN_ROLES, Role.DEPARTMENT_MANAGER, Role.DEPARTMENT_USER, Role.DATA_COLLECTOR],
    permissions: [AssignedPermission.EMPLOYEE],
    url: '/factory-database/employee/bulk-input',
    getFullUrl: function () {
      return appendQueryString(this.url);
    },
  },
  [Route.EMPLOYEE_COMMUTING]: {
    title: (t: NextTFunction) => t('common.employeeCommuting'),
    roles: [...PROJECT_ADMIN_ROLES, Role.DEPARTMENT_MANAGER, Role.DEPARTMENT_USER, Role.DATA_COLLECTOR],
    description: (t: NextTFunction) => t('common.infoBoxBDEmployeeCommuting'),
    permissions: [AssignedPermission.EMPLOYEE_COMMUTING],
    url: '/factory-database/employee-commuting',
    getFullUrl: function () {
      return appendQueryString(this.url);
    },
  },
  [Route.EMPLOYEE_COMMUTING_BULK]: {
    title: (t: NextTFunction) => t('bulk-input.bulkInput'),
    roles: [...PROJECT_ADMIN_ROLES, Role.DEPARTMENT_MANAGER, Role.DEPARTMENT_USER, Role.DATA_COLLECTOR],
    permissions: [AssignedPermission.EMPLOYEE_COMMUTING],
    url: '/factory-database/employee-commuting/bulk-input',
    getFullUrl: function () {
      return appendQueryString(this.url);
    },
  },
  [Route.EMPLOYEE_COMMUTING_CREATE]: {
    title: (t: NextTFunction) => t('common.addCommutingRoute'),
    roles: [...PROJECT_ADMIN_ROLES, Role.DEPARTMENT_MANAGER, Role.DEPARTMENT_USER, Role.DATA_COLLECTOR],
    permissions: [AssignedPermission.EMPLOYEE_COMMUTING],
    url: '/factory-database/employee-commuting/create',
    getFullUrl: function () {
      return appendQueryString(this.url);
    },
  },
  [Route.EMPLOYEE_COMMUTING_EDIT]: {
    title: (t: NextTFunction) => t('common.editCommutingRoute'),
    roles: [...PROJECT_ADMIN_ROLES, Role.DEPARTMENT_MANAGER, Role.DEPARTMENT_USER, Role.DATA_COLLECTOR],
    permissions: [AssignedPermission.EMPLOYEE_COMMUTING],
    url: '/factory-database/employee-commuting/edit',
    getFullUrl: function ({
      employeeTransportationId,
      scrollTo,
    }: {
      employeeTransportationId: string;
      scrollTo?: string;
    }) {
      const queryParam: Record<string, string> = { id: employeeTransportationId };
      if (scrollTo) {
        queryParam[SCROLL_TO_KEY] = 'true';
      }
      return appendQueryString(this.url, queryParam);
    },
  },
  [Route.MULTI_BOUNDARY_REPORT_EXPORT]: {
    title: (t: NextTFunction) => t('org-cfv-dashboard.combinedReport'),
    roles: PROJECT_ADMIN_ROLES,
    description: (t: NextTFunction) => t('common.infoBoxReport'),
    permissions: undefined,
    url: '/report/org-cfv/multi-report',
    getFullUrl: function () {
      return appendQueryString(this.url);
    },
  },

  [Route.AI_TABLE_MAPPING]: {
    title: (t: NextTFunction) => t('common.ai-mapping'),
    roles: PROJECT_ADMIN_ROLES,
    permissions: undefined,
    url: '/factory-database/raw-material/ai-mapping',
    getFullUrl: function (category: AI_MAPPING_CATEGORY) {
      return appendQueryString(this.url, { category });
    },
  },
  [Route.PROJECT_SETTINGS]: {
    title: (t: NextTFunction) => t('common.projectSettingsStep'),
    roles: [...PROJECT_ADMIN_ROLES, Role.DATA_COLLECTOR],
    description: (t: NextTFunction) => t('common.infoBoxProjectSetting'),
    permissions: undefined,
    url: '/project-settings',
    getFullUrl: function () {
      return appendQueryString(this.url);
    },
  },
  [Route.PROJECT_SETTINGS_EDIT]: {
    title: (t: NextTFunction) => t('common.editProjectInformation'),
    roles: PROJECT_ADMIN_ROLES,
    permissions: undefined,
    url: '/project-settings/edit',
    getFullUrl: function ({
      boundaryProjectId,
      openProductSeriesModal = 'false',
    }: {
      boundaryProjectId: string;
      openProductSeriesModal?: string;
    }) {
      return appendQueryString(this.url, { boundaryProjectId, openProductSeriesModal });
    },
  },
  [Route.PROJECT_SETTINGS_PRODUCT_SERIES]: {
    title: (t: NextTFunction) => t('fd-product-line.productSeries'),
    roles: [...PROJECT_ADMIN_ROLES, Role.DATA_COLLECTOR],
    permissions: undefined,
    url: '/project-settings/product-series',
    getFullUrl: function ({ projectId }: { projectId: string }) {
      return appendQueryString(this.url, { projectId });
    },
  },
  [Route.PROJECT_SETTINGS_PRODUCT_SERIES_DETAILS]: {
    title: (t: NextTFunction) => t('fd-scenarios.productSeriesDetail'),
    roles: [...PROJECT_ADMIN_ROLES, Role.DATA_COLLECTOR],
    permissions: undefined,
    url: '/project-settings/product-series/details',
    getFullUrl: function ({
      projectId,
      productSeriesId,
      type,
      refProductSeriesId,
    }: {
      projectId: string;
      productSeriesId: string;
      type?: DetailsPageTabType;
      refProductSeriesId?: string;
    }) {
      return appendQueryString(this.url, { projectId, productSeriesId, type, refProductSeriesId });
    },
  },
  [Route.BOUNDARY_PROJECT_MANAGEMENT]: {
    title: (t: NextTFunction) => t('common.projectManagement'),
    description: (t: NextTFunction) => t('common.infoBoxProjectMgmt'),
    roles: [...PROJECT_ADMIN_ROLES, Role.DATA_COLLECTOR],
    permissions: undefined,
    url: '/project-management',
    getFullUrl: function () {
      return appendQueryString(this.url, {}, false);
    },
  },
  [Route.BOUNDARY_PROJECT_MANAGEMENT_CREATE]: {
    title: (t: NextTFunction) => t('org-cfv-list-create-project.createNewProject'),
    roles: [Role.CMS_ADMIN, Role.ACCOUNT_ADMIN, Role.PROGRAM_MANAGER],
    permissions: undefined,
    url: '/project-management/create',
    getFullUrl: function () {
      return appendQueryString(this.url);
    },
  },
  [Route.DASHBOARD]: {
    title: (t: NextTFunction) => t('sidebar.dashboard'),
    roles: [Role.ACCOUNT_ADMIN, Role.PROGRAM_MANAGER],
    description: (t: NextTFunction) => t('common.infoBoxDashboard'),
    permissions: undefined,
    url: '/dashboard',
    getFullUrl: function () {
      return appendQueryString(this.url, {}, false);
    },
  },
  [Route.DASHBOARD_DEMO]: {
    title: (t: NextTFunction) => t('sidebar.dashboard'),
    roles: [Role.ACCOUNT_ADMIN, Role.PROGRAM_MANAGER],
    permissions: undefined,
    url: '/dashboard/demo',
    getFullUrl: function () {
      return appendQueryString(this.url, {}, false);
    },
  },
  [Route.DASHBOARD_UPLOAD_INVENTORY]: {
    title: (t: NextTFunction) => t('common.uploadInventory'),
    roles: [Role.ACCOUNT_ADMIN, Role.PROGRAM_MANAGER],
    permissions: undefined,
    url: '/dashboard/upload-inventory',
    getFullUrl: function () {
      return appendQueryString(this.url, {}, false);
    },
    isShowsUnsavedChangesModal: true,
  },
  [Route.DASHBOARD_CHECK_UPLOAD_INVENTORY_UPLOAD_INVENTORY]: {
    title: (t: NextTFunction) => t('common.uploadInventory'),
    roles: [Role.ACCOUNT_ADMIN, Role.PROGRAM_MANAGER],
    permissions: undefined,
    url: '/dashboard/check-upload-inventory/upload-inventory',
    getFullUrl: function () {
      return appendQueryString(this.url, {}, false);
    },
    isShowsUnsavedChangesModal: true,
  },
  [Route.DASHBOARD_PREVIEW_FILE_CREATE]: {
    title: (t: NextTFunction) => t('common.previewFile'),
    roles: [Role.ACCOUNT_ADMIN, Role.PROGRAM_MANAGER],
    permissions: undefined,
    url: '/dashboard/upload-inventory/preview-file',
    getFullUrl: function ({ jobId, fileId, fileName }: { jobId?: string | null; fileId: string; fileName: string }) {
      return appendQueryString(this.url, { jobId, fileId, fileName }, false);
    },
    isShowsUnsavedChangesModal: true,
  },
  [Route.DASHBOARD_CHECK_UPLOAD_INVENTORY_PREVIEW_FILE_CREATE]: {
    title: (t: NextTFunction) => t('common.previewFile'),
    roles: [Role.ACCOUNT_ADMIN, Role.PROGRAM_MANAGER],
    permissions: undefined,
    url: '/dashboard/check-upload-inventory/upload-inventory/preview-file',
    getFullUrl: function ({ jobId, fileId, fileName }: { jobId?: string | null; fileId: string; fileName: string }) {
      return appendQueryString(this.url, { jobId, fileId, fileName }, false);
    },
    isShowsUnsavedChangesModal: true,
  },
  [Route.DASHBOARD_CHECK_UPLOAD_INVENTOR_PREVIEW_FILE_EDIT]: {
    title: (t: NextTFunction) => t('common.editInventory'),
    roles: [Role.ACCOUNT_ADMIN, Role.PROGRAM_MANAGER],
    permissions: undefined,
    url: '/dashboard/check-upload-inventory/edit-inventory',
    getFullUrl: function ({ spaceId, inventoryId }: { spaceId?: string | null; inventoryId?: string | null }) {
      return appendQueryString(this.url, { spaceId, inventoryId }, false);
    },
    isShowsUnsavedChangesModal: true,
  },
  [Route.DASHBOARD_CHECK_UPLOAD_INVENTORY]: {
    title: (t: NextTFunction) => t('common.checkUploadInventory'),
    roles: [Role.ACCOUNT_ADMIN, Role.PROGRAM_MANAGER],
    permissions: undefined,
    url: '/dashboard/check-upload-inventory',
    getFullUrl: function () {
      return appendQueryString(this.url, {}, false);
    },
    isShowsUnsavedChangesModal: true,
  },
  [Route.EMISSION_OVERVIEW]: {
    title: (t: NextTFunction) => t('common.emissionOverview'),
    roles: [Role.ACCOUNT_ADMIN, Role.PROGRAM_MANAGER],
    permissions: undefined,
    url: '/emission-overview',
    getFullUrl: function () {
      return appendQueryString(this.url);
    },
  },
  // Wrap the configs into variables to make it easier to read
  ...ORGANIZATIONAL_STRUCTURE_MANAGEMENT_ROUTE_CONFIGS,
  ...SUPPLIER_MANAGEMENT_ROUTE_CONFIGS,
  ...PROJECT_FLOW_ROUTE_CONFIGS,
  ...MEMBER_CENTER_ROUTE_CONFIGS,
};

export const BOUNDARY_DATABASE_SIDEBAR: SidebarGroup[] = [
  {
    isGroup: false,
    children: [
      {
        key: Route.PRODUCT_LINE,
        assignedPermission: AssignedPermission.PRODUCT_LINE,
        testid: snakeCase(Route.PRODUCT_LINE),
      },
    ],
  },
  {
    isGroup: false,
    children: [{ key: Route.VENDOR, assignedPermission: AssignedPermission.VENDOR, testid: snakeCase(Route.VENDOR) }],
  },
  {
    isGroup: false,
    children: [
      {
        key: Route.RAW_MATERIAL,
        assignedPermission: AssignedPermission.RAW_MATERIAL,
        testid: snakeCase(Route.RAW_MATERIAL),
      },
    ],
  },
  {
    title: (t: NextTFunction) => t('sidebar.transportationInformation'),
    testid: snakeCase('transportationInformation'),
    isGroup: true,
    children: [
      {
        key: Route.RAW_MATERIAL_TRANSPORTATION,
        assignedPermission: AssignedPermission.RAW_MATERIAL_TRANSPORTATION,
        testid: snakeCase(Route.RAW_MATERIAL_TRANSPORTATION),
      },
      {
        key: Route.WASTE_TRANSPORTATION,
        assignedPermission: AssignedPermission.WASTE_TRANSPORTATION,
        testid: snakeCase(Route.WASTE_TRANSPORTATION),
      },
      {
        key: Route.DISTRIBUTION_TRANSPORTATION,
        assignedPermission: AssignedPermission.DISTRIBUTION_TRANSPORTATION,
        testid: snakeCase(Route.DISTRIBUTION_TRANSPORTATION),
      },
    ],
  },
  {
    isGroup: false,
    children: [
      {
        key: Route.SCENARIOS_CONFIGURATION,
        assignedPermission: AssignedPermission.SCENARIOS_CONFIGURATION,
        testid: snakeCase(Route.SCENARIOS_CONFIGURATION),
      },
    ],
  },
  {
    title: (t: NextTFunction) => t('common.employeeInformation'),
    testid: snakeCase('employeeInformation'),
    isGroup: true,
    children: [
      { key: Route.EMPLOYEE, assignedPermission: AssignedPermission.EMPLOYEE, testid: snakeCase(Route.EMPLOYEE) },
      {
        key: Route.EMPLOYEE_COMMUTING,
        assignedPermission: AssignedPermission.EMPLOYEE_COMMUTING,
        testid: snakeCase(Route.EMPLOYEE_COMMUTING),
      },
    ],
  },
  // {
  //   isGroup: false,
  //   children: [
  //     {
  //       key: Route.VENDOR_MASTER_TRIAL,
  //       assignedPermission: AssignedPermission.VENDOR,
  //       testid: snakeCase(Route.VENDOR_MASTER_TRIAL),
  //     },
  //   ],
  // },
  // {
  //   isGroup: false,
  //   children: [
  //     {
  //       key: Route.RAW_MATERIAL_TRANSPORTATION_TRIAL,
  //       assignedPermission: AssignedPermission.RAW_MATERIAL_TRANSPORTATION,
  //       testid: snakeCase(Route.RAW_MATERIAL_TRANSPORTATION_TRIAL),
  //     },
  //   ],
  // },
];

export const FACTORY_DATABASE_ROUTES: Route[] = Object.entries(ROUTE_CONFIG)
  .filter(([, value]) => value.url.startsWith('/factory-database'))
  .map(([key]) => key as Route);

export const ACCOUNT_MANAGEMENT_ROUTES: Route[] = Object.entries(ROUTE_CONFIG)
  .filter(([, value]) => value.url.startsWith('/account-management'))
  .map(([key]) => key as Route);

export const REPORT_ROUTES: Route[] = Object.entries(ROUTE_CONFIG)
  .filter(([, value]) => value.url.startsWith('/report'))
  .map(([key]) => key as Route);

export const BOUNDARY_PROJECT_MANAGEMENT_ROUTES: Route[] = Object.entries(ROUTE_CONFIG)
  .filter(([, value]) => value.url.startsWith('/project-management'))
  .map(([key]) => key as Route);
export const PROJECT_SETTINGS_ROUTES: Route[] = Object.entries(ROUTE_CONFIG)
  .filter(([, value]) => value.url.startsWith('/project-settings'))
  .map(([key]) => key as Route);

export const ORGANIZATIONAL_STRUCTURE_MANAGEMENT_ROUTES: Route[] = Object.keys(
  ORGANIZATIONAL_STRUCTURE_MANAGEMENT_ROUTE_CONFIGS,
) as Route[];
export const SUPPLIER_MANAGEMENT_ROUTES: Route[] = Object.keys(SUPPLIER_MANAGEMENT_ROUTE_CONFIGS) as Route[];

export const DASHBOARD_ROUTES: Route[] = Object.entries(ROUTE_CONFIG)
  .filter(([, value]) => value.url.startsWith('/dashboard'))
  .map(([key]) => key as Route);

export const MEMBER_CENTER_ROUTES: Route[] = Object.keys(MEMBER_CENTER_ROUTE_CONFIGS) as Route[];
export const PROJECT_FLOW_ROUTES: Route[] = Object.keys(PROJECT_FLOW_ROUTE_CONFIGS) as Route[];
export const IO_ROUTES: Route[] = Object.keys(ORG_CFV_ROUTE_CONFIGS) as Route[];
export const IP_ROUTES: Route[] = Object.keys(PRODUCT_CFV_ROUTE_CONFIGS) as Route[];

export const getRoutePath = (
  route: Route,
  query: Record<string, string | number | undefined | null> = {},
  withBoundaryId = true,
) => {
  return appendQueryString(ROUTE_CONFIG[route]?.url, query, withBoundaryId);
};
